import React, { useCallback, useEffect } from 'react';
import { useQueryLoader } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import AsyncGlobalIssueCreateModalTrigger from '@atlassian/jira-jsw-adapted-global-issue-create-trigger/src/async.tsx';
import { createAri } from '@atlassian/jira-platform-ari';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import triggerWithCustomFieldsJSWGlobalIssueCreateQuery, {
	type triggerWithCustomFieldsJSWGlobalIssueCreateQuery as triggerWithCustomFieldsJSWGlobalIssueCreateQueryType,
} from '@atlassian/jira-relay/src/__generated__/triggerWithCustomFieldsJSWGlobalIssueCreateQuery.graphql';
import { ANALYTICS_SOURCE_BACKLOG } from '../../../common/constants/analytics';
import { GlobalIssueCreateType } from '../../../model/modals/types';
import { useBacklogActionCreator, useBacklogSelector } from '../../../state';
import { closeModal } from '../../../state/actions/modals';
import { getCloudId, getRapidViewId } from '../../../state/selectors/app';
import {
	getPrefilledFieldsFromInputs,
	getSessionIdFromInputs,
	getSubmitErrorMessage,
} from '../../../state/selectors/global-issue-create';
import { getOpenModalData } from '../../../state/selectors/modals';

const GlobalIssueCreate = () => {
	const data = useBacklogSelector((state) => {
		const modalData = getOpenModalData(state);

		if (!modalData || modalData.type !== GlobalIssueCreateType) {
			return {
				fields: null,
				disableCreationSuccessFlag: true,
				sourceName: ANALYTICS_SOURCE_BACKLOG,
			};
		}

		return {
			fields: getPrefilledFieldsFromInputs(state),
			disableCreationSuccessFlag: true,
			sourceName: ANALYTICS_SOURCE_BACKLOG,
			submitErrorMessage: getSubmitErrorMessage(state),
			sessionId: getSessionIdFromInputs(state),
		};
	});
	const cloudId = useBacklogSelector(getCloudId);
	const boardId = useBacklogSelector(getRapidViewId);
	const closeModalAction = useBacklogActionCreator(closeModal);

	const onCreateSuccess = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			closeModalAction();
			fireTrackAnalytics(analyticsEvent, 'issueWithRequiredFields created', {
				createdMultipleIssues: analyticsEvent.payload.createdMultipleIssues,
			});
		},
		[closeModalAction],
	);
	const onCancel = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			closeModalAction();
			fireOperationalAnalytics(analyticsEvent, 'issueWithRequiredFields cancelled', {});
		},
		[closeModalAction],
	);

	const [queryReference, loadQuery] =
		useQueryLoader<triggerWithCustomFieldsJSWGlobalIssueCreateQueryType>(
			triggerWithCustomFieldsJSWGlobalIssueCreateQuery,
		);

	useEffect(() => {
		loadQuery(
			{
				boardId: createAri({
					resourceOwner: 'jira-software',
					cloudId,
					resourceType: 'board',
					resourceId: String(boardId),
				}),
			},
			{
				fetchPolicy: 'store-or-network',
			},
		);
	}, [boardId, cloudId, loadQuery]);

	return queryReference ? (
		<AsyncGlobalIssueCreateModalTrigger
			{...data}
			onCancel={onCancel}
			onCreateSuccess={onCreateSuccess}
			queryReference={queryReference}
		/>
	) : null;
};

export default GlobalIssueCreate;
